import type { Botconfig, ComponentConfig, CustomFontDefinition } from '@/types';
import objectPath from 'object-path';
import { defineStore } from 'pinia';

const themeProperties = {
  'widget.global.zindex': '9999',
  'widget.global.position.right': '',
  'widget.global.position.bottom': '',

  'widget.global.borderradius.inner': '6px',
  'widget.global.borderradius.outer': '12px',
  'widget.global.borderradius.shape': '50%',

  'widget.widget_button.background': '#0075BF', // CUSTOMER COLOR
  'widget.widget_button.textcolor': '#FFFFFF', // CUSTOMER COLOR

  'widget.header.background': '#0075BF', // CUSTOMER COLOR
  'widget.header.textcolor': '#FFFFFF', // CUSTOMER COLOR
  'widget.header.iconcolor': 'pink', // CUSTOMER COLOR same as textcolor

  'widget.operator.background': '#ffffff',
  'widget.operator.textcolor': '#222222',
  'widget.operator.avatarborder': '#ffffff',
  'widget.operator.logocolor': '#888888',

  'widget.coversation.background': '#e2e2e2',
  'widget.coversation.textcolor': '#222222',
  'widget.coversation.avatarborder': '#ffffff',
  'widget.coversation.message.textcolor': '#aaaaaa',
  'widget.coversation.message.dividercolor': '#aaaaaa',

  'messageTypes.bot.background': '#ffffff',
  'messageTypes.bot.textcolor': '#222222',
  'messageTypes.bot.button.border.color': '#aaaaaa',
  'messageTypes.userMessage.background': '#0075BF', // CUSTOMER COLOR
  'messageTypes.userMessage.textcolor': '#FFFFFF', // CUSTOMER COLOR

  'messageTypes.cards.background': '#ffffff',
  'messageTypes.cards.button.background': '#f3f3f3',
  'messageTypes.cards.button.textcolor': '#000000',
  'messageTypes.cards.button.bordercolor': '#dfdfdf',

  'messageTypes.carousel.background': '#000000',
  'messageTypes.carousel.navigation.background': '#ffffff',
  'messageTypes.carousel.navigation.textcolor': '#484848',

  'widget.teaser.bubble.background': '#ffffff',
  'widget.teaser.bubble.textcolor': '#222222',
  'widget.teaser.avatarborder.color': '#ffffff',
  'widget.teaser.qr.background': '#ffffff',
  'widget.teaser.qr.textcolor': '#222222',
  'widget.teaser.qr.border': '#ececec',
  'widget.teaser.qr.borderhover': '#aaaaaa',
  'widget.teaser.qr.icon.background': '#ffffff',
  'widget.teaser.qr.icon.color': '#888888',

  'widget.input.background': '#ececec',
  'widget.input.sendbutton.background': '#aaaaaa',
  'widget.input.sendbutton.icon': '#ffffff',

  'widget.input.textinput.background': '#ffffff',
  'widget.input.textinput.textcolor': '#222222',
  'widget.input.textinput.border': '#888888',

  'widget.qr.background': '#ffffff',
  'widget.qr.textcolor': '#222222',
  'widget.qr.border': '#aaaaaa',
  'widget.qr.borderhover': '#888888',
};

const themeVariablesMapping = {
  '--moin-widget-header-background': 'widget.header.background',
  '--moin-widget-header-textcolor': 'widget.header.textcolor',

  '--moin-widget-button-background': 'widget.widget_button.background',
  '--moin-widget-button-textcolor': 'widget.widget_button.textcolor',
  // cards within the carousel
  '--moin-message-cardbutton-background': 'messageTypes.card.button.background',
  '--moin-message-cardbutton-textcolor': 'messageTypes.card.button.textcolor',
  '--moin-message-cardbutton-bordercolor': 'messageTypes.card.button.border',
  '--moin-message-card-background': 'messageTypes.card.background',
  '--moin-message-carousel-button-background': 'messageTypes.carousel.background',
  /* '--moin-message-bot-background': 'messageTypes.textMessage.background',
  '--moin-message-bot-textcolor': 'messageTypes.textMessage.textcolor', */
};

export type ThemeVariables = Record<string, string>;

export const mapThemeToVariables = (theme: ComponentConfig) => {
  const variables: ThemeVariables = {};
  Object.keys(themeVariablesMapping).forEach((key) => {
    const mappingValue = themeVariablesMapping[key as keyof typeof themeVariablesMapping];
    const value = objectPath.get(theme, mappingValue);
    if (value) {
      variables[key] = value;
    }
  });
  return variables;
};

export const useThemeStore = defineStore('theme', {
  state: () => ({
    variables: {} as Record<string, string>,
    font: {} as CustomFontDefinition | undefined,
  }),
  getters: {
    getThemeVariables: (state) => state.variables,
    getCustomFont: (state) => state.font,
  },
  actions: {
    getThemeVariable(variable: string) {
      return this.variables[variable];
    },
    setConfig(config: Botconfig) {
      const components = config.components;
      const themeVariables: Record<string, string> = {};
      const keys = Object.keys(themeProperties);
      // we iterate over all possible theme properties
      // and check if the theme has a value for it
      // if it has a value we set the theme variable
      // --moin are the new ones --kw are the old legacy ones
      keys.forEach((key) => {
        const value = objectPath.get(components, key);
        if (value) {
          const cssvar = key.replace(/\./gi, '-');
          themeVariables[`--moin-${cssvar}`] = value;
          themeVariables[`--kw-${cssvar}`] = value;
        }
      });
      // because we some old names that are quite good
      // we map them to the new ones
      const mappedVars = mapThemeToVariables(components);
      Object.entries(mappedVars).forEach((item) => {
        const [key, value] = item;
        themeVariables[key] = value;
      });
      // assign the theme variables to the store
      this.variables = themeVariables;

      // Handle internal css variables overrides
      if (config.themeVariables) {
        Object.entries(config.themeVariables).forEach((item) => {
          const [key, value] = item;
          this.variables[key] = value;
        });
      }

      // HANLDE CUSTOM FONT
      const font = components?.widget?.global?.font;
      if (font) {
        this.setCustomFont(font);
      }
    },
    setCustomFont(font: CustomFontDefinition) {
      this.font = { ...font };
      if (font.fontfamily) this.variables['--userfont'] = font.fontfamily;
      if (font.fontSizeBase) this.variables['--moin-fontSizeBase'] = font.fontSizeBase;
    },
    setThemeVariables(variables: Record<string, string>) {
      Object.entries(variables).forEach((item) => {
        const [key, value] = item;
        this.variables[key] = value;
      });
    },
  },
});
